'use client'

import { ApolloLink, HttpLink } from '@apollo/client'
import { useRouter } from '@/navigation'
import { onError } from '@apollo/client/link/error'
import { setContext } from '@apollo/client/link/context'
import {
  ApolloNextAppProvider,
  NextSSRInMemoryCache,
  NextSSRApolloClient,
  SSRMultipartLink
} from '@apollo/experimental-nextjs-app-support/ssr'
import Bugsnag from '@bugsnag/js'
import { useSelector } from '@/hooks/redux'
import { CountryType } from '@/reducers/workflow'

const sessionLink = setContext((_, { headers }) => {
  if (
    sessionStorage.getItem('contactSecurityToken') !== null ||
    sessionStorage.getItem('prospectSecurityToken') !== null
  ) {
    return {
      headers: {
        ...headers,
        'X-Security-Token':
          sessionStorage.getItem('contactSecurityToken') ||
          sessionStorage.getItem('prospectSecurityToken')
      }
    }
  } else {
    return { headers: { ...headers } }
  }
})

const whitelistedErrorOperations = [
  'applyBillingCycle',
  'applyPromotion',
  'authenticate',
  'createUser',
  'formalitiesPage'
]
// @ts-expect-error
const errorLink = (router) =>
  onError(({ graphQLErrors, networkError, operation }) => {
    if (
      graphQLErrors &&
      !whitelistedErrorOperations.includes(operation.operationName)
    ) {
      router.push(`/error?message=${graphQLErrors[0].message}`)
      graphQLErrors.forEach((error) => Bugsnag.notify(error.message))
    }
    if (networkError) {
      Bugsnag.notify(networkError)
      router.push(`/error?message=${encodeURIComponent(networkError.message)}`)
    }
  })

const host = (country: CountryType) => {
  if (country === 'FR') {
    return process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT_FR
  } else if (country === 'BE') {
    return process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT_BE
  }
}

// @ts-expect-error
const makeClient = (router, country) => () => {
  const httpLink = new HttpLink({
    uri: host(country),
    credentials: 'include',
    fetchOptions: { cache: 'no-store' }
  })

  return new NextSSRApolloClient({
    // use the `NextSSRInMemoryCache`, not the normal `InMemoryCache`
    cache: new NextSSRInMemoryCache(),
    link:
      typeof window === 'undefined'
        ? ApolloLink.from([
            errorLink(router),
            // in a SSR environment, if you use multipart features like
            // @defer, you need to decide how to handle these.
            // This strips all interfaces with a `@defer` directive from your queries.
            new SSRMultipartLink({
              stripDefer: true
            }),
            sessionLink.concat(httpLink)
          ])
        : ApolloLink.from([errorLink(router), sessionLink.concat(httpLink)])
  })
}

export function ApolloWrapper({ children }: React.PropsWithChildren) {
  const router = useRouter()
  const { country } = useSelector((state) => state.workflow)

  return (
    <ApolloNextAppProvider makeClient={makeClient(router, country)}>
      {children}
    </ApolloNextAppProvider>
  )
}
